import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '@services/api';
import { useSignalR } from '@hooks/useSignalR';
import { useTranslation } from '@stores/translationStore';
import { useSessionStore } from '@stores/sessionStore';
import StatusCard, { StatusCardMetadata } from '@components/StatusCard';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import './NotificationsPage.scss';

interface ActivityItem {
  type: string;
  referenceId: string;
  status: string;
  stationName?: string;
  items?: { name: string; quantity: number; price?: number }[];
  totalAmount?: number;
  requestType?: string;
  itemName?: string;
  quantity?: number;
  roomNumber?: string;
  subject?: string;
  category?: string;
  priority?: string;
  reservationType?: string;
  date?: string;
  time?: string;
  partySize?: number;
  specialRequests?: string;
  rejectionReason?: string;
  description?: string;
  scheduledFor?: string;
  orderNumber?: string;
  requestedBy?: string;
  createdAt: string;
  updatedAt?: string;
}

interface VoucherItem {
  id: string;
  name: string;
  description?: string;
  valueType?: string;
  valueAmount?: number;
  status: string;
  currentRedemptions: number;
  maxRedemptions: number;
  expiresAt?: string;
  issuedAt: string;
  redemptions?: { facility?: string; redeemedAt: string; notes?: string }[];
}

interface ActivityData {
  active: ActivityItem[];
  vouchers: VoucherItem[];
  completed: ActivityItem[];
  hotelSlug: string;
  isVerifierDevice?: boolean;
  deviceNames?: string[];
}

type FilterCategory = 'all' | 'orders' | 'housekeeping' | 'maintenance' | 'reservations' | 'services' | 'vouchers';

const FILTER_TABS: { key: FilterCategory; icon: string; label: string }[] = [
  { key: 'all', icon: '📋', label: 'All' },
  { key: 'orders', icon: '🍽️', label: 'Orders' },
  { key: 'housekeeping', icon: '🛎️', label: 'Housekeeping' },
  { key: 'maintenance', icon: '🔧', label: 'Maintenance' },
  { key: 'reservations', icon: '📅', label: 'Reservations' },
  { key: 'services', icon: '🔔', label: 'Services' },
  { key: 'vouchers', icon: '🎫', label: 'Vouchers' },
];

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { on } = useSignalR();
  const personalName = useSessionStore(s => s.personalName);
  const [data, setData] = useState<ActivityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterCategory>('all');
  const [personTab, setPersonTab] = useState<string>('all');

  const loadActivity = useCallback(async (signal?: AbortSignal) => {
    try {
      const params = filter !== 'all' ? `?category=${filter}` : '';
      const { data: result } = await apiClient.get(`/api/v1/guest/activity${params}`, { signal });
      setData(result);
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    const controller = new AbortController();
    loadActivity(controller.signal);
    return () => controller.abort();
  }, [loadActivity]);

  // Real-time updates: refresh activity when any status changes
  useEffect(() => {
    const events = [
      'OrderStatusChanged',
      'RequestStatusChanged',
      'ReportStatusChanged',
      'ReservationStatusChanged',
      'ServiceRequestStatusChanged',
      'VoucherReceived',
    ] as const;

    const unsubs = events.map(event =>
      on(event, (eventData: Record<string, unknown>) => {
        // Update in-place for status changes
        setData(prev => {
          if (!prev) return prev;

          // For voucher received, just reload
          if (event === 'VoucherReceived') {
            loadActivity();
            return prev;
          }

          const idField: string = event === 'OrderStatusChanged' ? 'orderId'
            : event === 'ReportStatusChanged' ? 'reportId'
            : 'requestId';
          const entityId = String(eventData[idField]);
          const newStatus = String(eventData.status);

          // Map event to entity type
          const typeMap: Record<string, string> = {
            OrderStatusChanged: 'order',
            RequestStatusChanged: 'housekeeping',
            ReportStatusChanged: 'maintenance',
            ReservationStatusChanged: 'reservation',
            ServiceRequestStatusChanged: 'service',
          };
          const entityType = typeMap[event];

          // Find and update the item
          const updateItem = (item: ActivityItem) => {
            if (item.type === entityType && item.referenceId === entityId) {
              return { ...item, status: newStatus, updatedAt: new Date().toISOString() };
            }
            return item;
          };

          // Check if item moved from active to completed
          const isTerminal = (type: string, status: string) => {
            const s = status.toLowerCase();
            switch (type) {
              case 'order': return ['delivered', 'completed', 'cancelled', 'rejected'].includes(s);
              case 'housekeeping': return ['delivered', 'completed', 'cancelled'].includes(s);
              case 'maintenance': return ['resolved', 'closed'].includes(s);
              case 'reservation': return ['confirmed', 'completed', 'cancelled', 'declined', 'noshow'].includes(s);
              case 'service': return ['completed', 'cancelled'].includes(s);
              default: return false;
            }
          };

          let newActive = prev.active.map(updateItem);
          let newCompleted = prev.completed.map(updateItem);

          // Move items from active -> completed if terminal
          const movedItems = newActive.filter(item =>
            item.type === entityType && item.referenceId === entityId && isTerminal(item.type, item.status)
          );
          if (movedItems.length > 0) {
            newActive = newActive.filter(item => !movedItems.includes(item));
            newCompleted = [...movedItems, ...newCompleted];
          }

          return { ...prev, active: newActive, completed: newCompleted };
        });
      })
    );

    return () => unsubs.forEach(u => u());
  }, [on, loadActivity]);

  const toStatusCardMetadata = (item: ActivityItem): StatusCardMetadata => ({
    type: item.type as StatusCardMetadata['type'],
    referenceId: item.referenceId,
    status: item.status,
    stationName: item.stationName,
    items: item.items,
    totalAmount: item.totalAmount,
    requestType: item.requestType,
    itemName: item.itemName,
    quantity: item.quantity,
    roomNumber: item.roomNumber,
    subject: item.subject,
    category: item.category,
    priority: item.priority,
    reservationType: item.reservationType,
    date: item.date,
    time: item.time,
    partySize: item.partySize,
    specialRequests: item.specialRequests,
    rejectionReason: item.rejectionReason,
    description: item.description,
    scheduledFor: item.scheduledFor,
    orderNumber: item.orderNumber,
    requestedBy: item.requestedBy,
  });

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return t('common.justNow');
    if (diffMins < 60) return t('common.minutesAgo', { n: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('common.hoursAgo', { n: diffHours });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return <PageSpinner />;
  }

  const active = data?.active || [];
  const vouchers = data?.vouchers || [];
  const completed = data?.completed || [];

  // Verifier device: show person tabs and allow filtering
  const isVerifier = data?.isVerifierDevice && (data?.deviceNames?.length ?? 0) > 0;
  const deviceNames = data?.deviceNames || [];

  // Filter by person tab
  const filterByPerson = (items: ActivityItem[]) => {
    if (!isVerifier || personTab === 'all') return items;
    return items.filter(i => i.requestedBy === personTab || (!i.requestedBy && personTab === (personalName || 'all')));
  };

  const filteredActive = filterByPerson(active);
  const filteredCompleted = filterByPerson(completed);

  const showVouchers = filter === 'all' || filter === 'vouchers';
  const showActive = filter !== 'vouchers';
  const showCompleted = filter !== 'vouchers';

  const hasContent = filteredActive.length > 0 || vouchers.length > 0 || filteredCompleted.length > 0;

  return (
    <div className="activity-page">
      {/* Header */}
      <PageHeader title={<>📋 {t('activity.title') || 'My Activity'}</>} className="activity-header">
        <div className="activity-filters">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              className={`activity-filter-btn ${filter === tab.key ? 'active' : ''}`}
              onClick={() => { setFilter(tab.key); setLoading(true); }}
            >
              {tab.icon} {t(`activity.filter.${tab.key}`) || tab.label}
            </button>
          ))}
        </div>
      </PageHeader>

      {/* Person tabs (verifier device only) */}
      {isVerifier && deviceNames.length > 1 && (
        <div className="activity-person-tabs">
          <button
            className={`activity-person-tab ${personTab === 'all' ? 'active' : ''}`}
            onClick={() => setPersonTab('all')}
          >
            👥 {t('activity.allPeople')}
          </button>
          {deviceNames.map(name => (
            <button
              key={name}
              className={`activity-person-tab ${personTab === name ? 'active' : ''}`}
              onClick={() => setPersonTab(name)}
            >
              {name === personalName ? `⭐ ${name}` : name}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="activity-content" aria-live="polite">
        {!hasContent ? (
          <div className="activity-empty">
            <span className="activity-empty-icon">✨</span>
            <p>{t('activity.empty') || 'No activity yet'}</p>
            <span className="activity-empty-sub">{t('activity.emptySub') || 'Your requests and orders will appear here'}</span>
          </div>
        ) : (
          <>
            {/* Active Requests */}
            {showActive && filteredActive.length > 0 && (
              <section className="activity-section">
                <div className="activity-section-header">
                  <span className="activity-section-dot activity-section-dot--active" />
                  <h3>{t('activity.active') || 'Active Requests'}</h3>
                  <span className="activity-section-count">{filteredActive.length}</span>
                </div>
                <div className="activity-cards">
                  {filteredActive.map(item => (
                    <div key={`${item.type}-${item.referenceId}`} className="activity-card-wrapper">
                      <StatusCard metadata={toStatusCardMetadata(item)} />
                      <div className="activity-card-meta">
                        {isVerifier && item.requestedBy && (
                          <span className="activity-card-person">👤 {item.requestedBy}</span>
                        )}
                        <span className="activity-card-time">{formatRelativeTime(item.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Vouchers */}
            {showVouchers && vouchers.length > 0 && (
              <section className="activity-section">
                <div className="activity-section-header">
                  <span className="activity-section-dot activity-section-dot--voucher" />
                  <h3>{t('activity.vouchers') || 'My Vouchers'}</h3>
                  <span className="activity-section-count">{vouchers.length}</span>
                </div>
                <div className="activity-vouchers">
                  {vouchers.map(v => (
                    <VoucherCard key={v.id} voucher={v} />
                  ))}
                </div>
              </section>
            )}

            {/* Completed */}
            {showCompleted && filteredCompleted.length > 0 && (
              <section className="activity-section activity-section--completed">
                <div className="activity-section-header">
                  <span className="activity-section-dot activity-section-dot--completed" />
                  <h3>{t('activity.completed') || 'Completed'}</h3>
                  <span className="activity-section-count">{filteredCompleted.length}</span>
                </div>
                <div className="activity-cards activity-cards--completed">
                  {filteredCompleted.map(item => (
                    <div key={`${item.type}-${item.referenceId}`} className="activity-card-wrapper">
                      <StatusCard metadata={toStatusCardMetadata(item)} />
                      <div className="activity-card-meta">
                        {isVerifier && item.requestedBy && (
                          <span className="activity-card-person">👤 {item.requestedBy}</span>
                        )}
                        <span className="activity-card-time">{formatRelativeTime(item.updatedAt || item.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Voucher Card Component ──
function VoucherCard({ voucher }: { voucher: VoucherItem }) {
  const { t } = useTranslation();
  const isActive = voucher.status === 'Active';
  const isFullyRedeemed = voucher.currentRedemptions >= voucher.maxRedemptions;
  const isExpired = voucher.expiresAt && new Date(voucher.expiresAt) < new Date();
  const usagePercent = voucher.maxRedemptions > 0
    ? Math.min(100, Math.round((voucher.currentRedemptions / voucher.maxRedemptions) * 100))
    : 0;

  const statusLabel = isExpired ? t('voucher.statusExpired') : isFullyRedeemed ? t('voucher.statusFullyUsed') : isActive ? t('voucher.statusActive') : voucher.status;
  const statusClass = isExpired || isFullyRedeemed ? 'used' : isActive ? 'active' : 'inactive';

  return (
    <div className={`voucher-card voucher-card--${statusClass}`}>
      <div className="voucher-card__header">
        <span className="voucher-card__icon">🎫</span>
        <span className="voucher-card__name">{voucher.name}</span>
        <span className={`voucher-card__status voucher-card__status--${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      {voucher.description && (
        <p className="voucher-card__desc">{voucher.description}</p>
      )}

      {voucher.valueAmount != null && voucher.valueType && (
        <div className="voucher-card__value">
          {voucher.valueType === 'Percentage' ? t('voucher.percentOff', { amount: voucher.valueAmount }) :
           voucher.valueType === 'Fixed' ? t('voucher.fixedValue', { amount: `₪${voucher.valueAmount.toFixed(2)}` }) :
           voucher.valueType === 'FreeItem' ? t('voucher.freeItem') : `${voucher.valueAmount}`}
        </div>
      )}

      {/* Usage progress */}
      <div className="voucher-card__usage">
        <div className="voucher-card__usage-bar">
          <div className="voucher-card__usage-fill" style={{ width: `${usagePercent}%` }} />
        </div>
        <span className="voucher-card__usage-text">
          {t('voucher.usedCount', { used: voucher.currentRedemptions, max: voucher.maxRedemptions })}
        </span>
      </div>

      {/* Redemption history */}
      {voucher.redemptions && voucher.redemptions.length > 0 && (
        <div className="voucher-card__redemptions">
          {voucher.redemptions.slice(0, 3).map((r, i) => (
            <div key={i} className="voucher-card__redemption">
              <span>✓ {r.facility || t('voucher.redeemed')}</span>
              <span className="voucher-card__redemption-time">
                {new Date(r.redeemedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
              </span>
            </div>
          ))}
        </div>
      )}

      {voucher.expiresAt && (
        <div className="voucher-card__expires">
          {t('voucher.expires')}: {new Date(voucher.expiresAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      )}
    </div>
  );
}
